/**
 * Příjemka — rozšíření řádků košíku na payload pro `stock/add`.
 * Metrové materiály (`m`, …): quantity = délka kusu (m), pieceCount = počet kusů stejné délky.
 * Stejný SAP s různými délkami = více řádků košíku (každý se expanduje zvlášť).
 *
 * Ceník `Cena za MJ` u `m`/`bm` je **Kč/m** (ne Kč/role). `unit_price` na WI / příjemce
 * je stejná MJ: FIFO výdej metráže = odebrané m × Kč/m.
 */

import { isWarehousePieceLengthUnit } from './material-quantity.util';

export interface WarehouseReceiptCartLineInput {
  sapCode: string;
  quantity: number;
  jednotka?: string | null;
  /** Počet kusů stejné délky u metrových materiálů (jen FE košík). */
  pieceCount?: number | null;
  unitPrice?: number | null;
  supplierName?: string | null;
  /** Přesné textové označení šarže; neprovádí se trim ani normalizace. */
  batchNumber?: string | null;
}

export interface WarehouseReceiptStockItem {
  sapCode: string;
  quantity: number;
  unitPrice?: number;
  supplierName?: string;
  batchNumber?: string | null;
}

function positiveFinite(n: number): boolean {
  return Number.isFinite(n) && n > 0;
}

function normalizeMeterPieceCount(raw: number | null | undefined): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

function roundReceiptMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Množství, kterým se násobí ceníková `unit_price` (Kč / nákupní MJ).
 * U metrových rolí = kusů × délka (celkové metry); jinak nákupní `quantity`.
 */
export function warehouseReceiptPricedQuantity(
  line: Pick<WarehouseReceiptCartLineInput, 'quantity' | 'jednotka' | 'pieceCount'>,
): number | null {
  const qty = Number(line.quantity);
  if (!positiveFinite(qty)) return null;
  if (isWarehousePieceLengthUnit(line.jednotka)) {
    return qty * normalizeMeterPieceCount(line.pieceCount);
  }
  return qty;
}

/**
 * CELKEM řádku příjemky. Metrové: kusů × round(Kč/m × délka) — stejné jako N WI po expandu.
 */
export function warehouseReceiptCartLineTotal(
  line: Pick<WarehouseReceiptCartLineInput, 'quantity' | 'jednotka' | 'pieceCount' | 'unitPrice'>,
): number | null {
  if (line.unitPrice == null) return null;
  const price = Number(line.unitPrice);
  if (!Number.isFinite(price) || price < 0) return null;
  if (isWarehousePieceLengthUnit(line.jednotka)) {
    const length = Number(line.quantity);
    if (!positiveFinite(length)) return null;
    const pieces = normalizeMeterPieceCount(line.pieceCount);
    const perPiece = roundReceiptMoney(price * length);
    return roundReceiptMoney(perPiece * pieces);
  }
  const qty = Number(line.quantity);
  if (!positiveFinite(qty)) return null;
  return roundReceiptMoney(price * qty);
}

/**
 * Metrové příjemky: pieceCount musí být celé číslo ≥ 1.
 * Vrací chybovou zprávu nebo `null` pokud OK.
 */
export function wholeWarehouseMeterPieceCountError(
  lines: ReadonlyArray<WarehouseReceiptCartLineInput>,
): string | null {
  for (const line of lines) {
    if (!isWarehousePieceLengthUnit(line.jednotka)) continue;
    const sap = String(line.sapCode ?? '').trim() || '?';
    const count = Number(line.pieceCount ?? 1);
    if (!Number.isFinite(count) || count < 1 || !Number.isInteger(count)) {
      return (
        `SAP ${sap}: u metrového materiálu zadejte celý počet kusů (≥ 1)` +
        (Number.isFinite(count) ? `, ne ${count}` : '')
      );
    }
    const length = Number(line.quantity);
    if (!positiveFinite(length)) {
      return `SAP ${sap}: délka kusu (m) musí být > 0`;
    }
  }
  return null;
}

/**
 * Jeden řádek košíku → jeden nebo více API řádků (N × délka u `m`).
 */
export function expandWarehouseReceiptStockItems(
  lines: ReadonlyArray<WarehouseReceiptCartLineInput>,
): WarehouseReceiptStockItem[] {
  const out: WarehouseReceiptStockItem[] = [];
  for (const line of lines) {
    const sapCode = String(line.sapCode ?? '').trim();
    const qty = Number(line.quantity);
    if (!sapCode || !positiveFinite(qty)) continue;

    const unitPrice =
      line.unitPrice != null && Number.isFinite(Number(line.unitPrice))
        ? Number(line.unitPrice)
        : undefined;
    const supplierName = String(line.supplierName ?? '').trim() || undefined;
    const hasBatchNumber = typeof line.batchNumber === 'string';

    if (isWarehousePieceLengthUnit(line.jednotka)) {
      const pieceCount = normalizeMeterPieceCount(line.pieceCount);
      for (let i = 0; i < pieceCount; i++) {
        out.push({
          sapCode,
          quantity: qty,
          ...(unitPrice != null ? { unitPrice } : {}),
          ...(supplierName ? { supplierName } : {}),
          ...(hasBatchNumber ? { batchNumber: line.batchNumber } : {}),
        });
      }
      continue;
    }

    out.push({
      sapCode,
      quantity: qty,
      ...(unitPrice != null ? { unitPrice } : {}),
      ...(supplierName ? { supplierName } : {}),
      ...(hasBatchNumber ? { batchNumber: line.batchNumber } : {}),
    });
  }
  return out;
}

/** Řádek dokladu (příjemka / převodka) před agregací pro zobrazení. */
export interface WarehouseDocumentLineForAggregate {
  sapCode?: string | null;
  productName?: string | null;
  quantity: number;
  unit?: string | null;
  unitPrice?: number | null;
  lineTotal?: number | null;
  supplierName?: string | null;
  batchNumber?: string | null;
  objectId?: string | null;
  objectIds?: string[] | null;
}

/**
 * Agregovaný řádek pro detail dokladu — stejný SAP + délka + cena = jeden řádek
 * (`20× 100 m`), i když DB / API má N WarehouseItem řádků.
 */
export interface WarehouseDocumentDisplayLine {
  sapCode: string;
  productName: string | null;
  /** Délka kusu (m) u metrových; jinak množství jednoho zdrojového řádku. */
  quantity: number;
  unit: string | null;
  unitPrice: number | null;
  /** Počet sloučených kusů (rolí) se stejnou délkou a cenou. */
  pieceCount: number;
  /** Součet lineTotal skupiny (kusů × délka × Kč/m u metrových). */
  lineTotal: number | null;
  supplierName: string | null;
  batchNumber: string | null;
  objectId?: string | null;
  objectIds?: string[] | null;
}

function unitPriceKey(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(Number(price))) return '';
  return String(Math.round(Number(price) * 10000) / 10000);
}

/**
 * Slučuje identické kusy (typicky metrové role po expandu) pro UI detail/list.
 * Klíč: SAP + quantity (délka) + unitPrice + unit + supplier.
 */
export function aggregateWarehouseDocumentLinesForDisplay(
  items: ReadonlyArray<WarehouseDocumentLineForAggregate>,
): WarehouseDocumentDisplayLine[] {
  const order: string[] = [];
  const map = new Map<string, WarehouseDocumentDisplayLine>();

  for (const item of items) {
    const sapCode = String(item.sapCode ?? '').trim();
    const quantity = Number(item.quantity);
    if (!sapCode || !positiveFinite(quantity)) continue;

    const unit = (item.unit ?? '').trim() || null;
    const unitPrice =
      item.unitPrice != null && Number.isFinite(Number(item.unitPrice))
        ? Number(item.unitPrice)
        : null;
    const supplierName = String(item.supplierName ?? '').trim() || null;
    const batchNumber = typeof item.batchNumber === 'string' ? item.batchNumber : null;
    const objectId = String(item.objectId ?? '').trim() || null;
    const objectIdsKey =
      item.objectIds?.length && item.objectIds.length > 1
        ? [...item.objectIds].map((id) => String(id ?? '').trim()).filter(Boolean).sort().join(',')
        : '';
    const key = JSON.stringify([
      sapCode,
      String(quantity),
      unitPriceKey(unitPrice),
      (unit ?? '').toLowerCase(),
      supplierName ?? '',
      batchNumber,
      objectId ?? objectIdsKey,
    ]);

    const rowLineTotal =
      item.lineTotal != null && Number.isFinite(Number(item.lineTotal))
        ? Number(item.lineTotal)
        : unitPrice != null
          ? roundReceiptMoney(unitPrice * quantity)
          : null;

    const existing = map.get(key);
    if (existing) {
      existing.pieceCount += 1;
      if (rowLineTotal != null) {
        existing.lineTotal = roundReceiptMoney((existing.lineTotal ?? 0) + rowLineTotal);
      }
      if (!existing.productName) {
        const name = String(item.productName ?? '').trim();
        if (name) existing.productName = name;
      }
      continue;
    }

    const lineObjectIds =
      item.objectIds?.length && item.objectIds.length > 1
        ? [...item.objectIds].map((id) => String(id ?? '').trim()).filter(Boolean)
        : undefined;

    order.push(key);
    map.set(key, {
      sapCode,
      productName: String(item.productName ?? '').trim() || null,
      quantity,
      unit,
      unitPrice,
      pieceCount: 1,
      lineTotal: rowLineTotal,
      supplierName,
      batchNumber,
      ...(objectId ? { objectId } : {}),
      ...(lineObjectIds?.length ? { objectIds: lineObjectIds } : {}),
    });
  }

  return order.map((k) => map.get(k)!);
}

/** Popisek množství: `20× 100 m`, `100 m`, `5 ks`. */
export function formatWarehouseDocumentDisplayQty(
  line: Pick<WarehouseDocumentDisplayLine, 'quantity' | 'unit' | 'pieceCount'>,
): string {
  const qty = Number(line.quantity);
  const pieces = Math.max(1, Math.floor(Number(line.pieceCount) || 1));
  if (isWarehousePieceLengthUnit(line.unit)) {
    return pieces > 1 ? `${pieces}× ${qty} m` : `${qty} m`;
  }
  const unit = (line.unit ?? '').trim();
  const base = unit ? `${qty} ${unit}` : String(qty);
  return pieces > 1 ? `${pieces}× ${base}` : base;
}

/**
 * Souhrn materiálů přes více dokladů (výdej / příjem / převod) — klíč SAP + jednotka.
 * U metrových sčítá délky (m); u kusových sčítá quantity.
 */
export interface WarehouseMaterialsSummaryLine {
  sapCode: string;
  productName: string | null;
  quantity: number;
  unit: string | null;
  unitPrice: number | null;
  lineTotal: number | null;
}

export function aggregateWarehouseMaterialsSummary(
  items: ReadonlyArray<WarehouseDocumentLineForAggregate>,
): WarehouseMaterialsSummaryLine[] {
  const order: string[] = [];
  const map = new Map<
    string,
    WarehouseMaterialsSummaryLine & { _pricedQty: number; _priceSum: number }
  >();

  for (const item of items) {
    const sapCode = String(item.sapCode ?? '').trim();
    const quantity = Number(item.quantity);
    if (!sapCode || !positiveFinite(quantity)) continue;

    const unit = (item.unit ?? '').trim() || null;
    const unitNorm = (unit ?? '').toLowerCase();
    const key = `${sapCode}\0${unitNorm}`;
    const unitPrice =
      item.unitPrice != null && Number.isFinite(Number(item.unitPrice))
        ? Number(item.unitPrice)
        : null;
    const rowLineTotal =
      item.lineTotal != null && Number.isFinite(Number(item.lineTotal))
        ? Number(item.lineTotal)
        : unitPrice != null
          ? roundReceiptMoney(unitPrice * quantity)
          : null;

    const existing = map.get(key);
    if (existing) {
      existing.quantity = roundReceiptMoney(existing.quantity + quantity);
      if (rowLineTotal != null) {
        existing.lineTotal = roundReceiptMoney((existing.lineTotal ?? 0) + rowLineTotal);
      }
      if (unitPrice != null) {
        existing._pricedQty += quantity;
        existing._priceSum += unitPrice * quantity;
        existing.unitPrice =
          existing._pricedQty > 0
            ? roundReceiptMoney(existing._priceSum / existing._pricedQty)
            : existing.unitPrice;
      }
      if (!existing.productName) {
        const name = String(item.productName ?? '').trim();
        if (name) existing.productName = name;
      }
      continue;
    }

    order.push(key);
    map.set(key, {
      sapCode,
      productName: String(item.productName ?? '').trim() || null,
      quantity,
      unit,
      unitPrice,
      lineTotal: rowLineTotal,
      _pricedQty: unitPrice != null ? quantity : 0,
      _priceSum: unitPrice != null ? unitPrice * quantity : 0,
    });
  }

  return order.map((k) => {
    const row = map.get(k)!;
    return {
      sapCode: row.sapCode,
      productName: row.productName,
      quantity: row.quantity,
      unit: row.unit,
      unitPrice: row.unitPrice,
      lineTotal: row.lineTotal,
    };
  });
}

/** Popisek množství v souhrnu: `3000 m`, `15 ks`. */
export function formatWarehouseMaterialsSummaryQty(
  line: Pick<WarehouseMaterialsSummaryLine, 'quantity' | 'unit'>,
): string {
  const qty = Number(line.quantity);
  if (!Number.isFinite(qty)) return '—';
  if (isWarehousePieceLengthUnit(line.unit)) return `${qty} m`;
  const unit = (line.unit ?? '').trim();
  return unit ? `${qty} ${unit}` : String(qty);
}

export interface WarehouseStockNoteLineInput {
  sapCode: string;
  quantity: number;
  /** Katalogová / WI jednotka — u `m` agreguje kusy stejné délky. */
  unit?: string | null;
}

/**
 * Kompaktní tělo poznámky z expandovaných řádků:
 * `320333: 20×100 m, 20×200 m; ABC×5`
 */
export function formatCompactWarehouseStockNoteBody(
  lines: ReadonlyArray<WarehouseStockNoteLineInput>,
): string {
  type SapBucket = {
    unit: string | null;
    /** Metrové: délka → počet kusů. */
    lengths: Map<number, number>;
    /** Nemetrové: součet quantity. */
    totalQty: number;
  };
  const order: string[] = [];
  const bySap = new Map<string, SapBucket>();

  for (const line of lines) {
    const sap = String(line.sapCode ?? '').trim();
    const qty = Number(line.quantity);
    if (!sap || !positiveFinite(qty)) continue;
    const unit = (line.unit ?? '').trim() || null;

    let bucket = bySap.get(sap);
    if (!bucket) {
      bucket = { unit, lengths: new Map(), totalQty: 0 };
      bySap.set(sap, bucket);
      order.push(sap);
    } else if (!bucket.unit && unit) {
      bucket.unit = unit;
    }

    if (isWarehousePieceLengthUnit(bucket.unit ?? unit)) {
      bucket.lengths.set(qty, (bucket.lengths.get(qty) ?? 0) + 1);
    } else {
      bucket.totalQty += qty;
    }
  }

  const parts: string[] = [];
  for (const sap of order) {
    const bucket = bySap.get(sap)!;
    if (isWarehousePieceLengthUnit(bucket.unit)) {
      const bits = [...bucket.lengths.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([len, count]) => (count > 1 ? `${count}×${len} m` : `${len} m`));
      if (bits.length) parts.push(`${sap}: ${bits.join(', ')}`);
      continue;
    }
    if (bucket.totalQty > 0) parts.push(`${sap}×${bucket.totalQty}`);
  }
  return parts.join('; ');
}

export function formatWarehouseReceiptAutoDescription(
  lines: ReadonlyArray<WarehouseStockNoteLineInput>,
  prefix: 'TelcoTrack příjem' | 'Příjem' = 'TelcoTrack příjem',
): string {
  const body = formatCompactWarehouseStockNoteBody(lines);
  return body ? `${prefix} ${body}` : prefix;
}

export function formatWarehouseIssueAutoDescription(
  lines: ReadonlyArray<WarehouseStockNoteLineInput>,
): string {
  const body = formatCompactWarehouseStockNoteBody(lines);
  return body ? `TelcoTrack výdej ${body}` : 'TelcoTrack výdej';
}

/**
 * Historické auto-názvy převodek s raw Espo ID skladů — nehodí se do UI/PDF.
 * Např. `TelcoTrack transfer abc->def` / `TelcoTrack WarehouseTransfer from=abc to=def`.
 */
export function isWarehouseTransferAutoIdLabel(text: string | null | undefined): boolean {
  const raw = (text ?? '').trim();
  if (!raw) return false;
  return (
    /^TelcoTrack transfer \S+(?:->|→)\S+$/i.test(raw) ||
    /^TelcoTrack WarehouseTransfer from=\S+ to=\S+$/i.test(raw)
  );
}

/**
 * Titulek převodky v seznamu/detailu (jako příjemka: číslo dokladu, ne ID skladů).
 * Vlastní `name` ponechá; auto-label s ID nahradí zkráceným id dokladu.
 */
export function formatWarehouseTransferListTitle(params: {
  id: string;
  name?: string | null;
}): string {
  const name = (params.name ?? '').trim();
  if (name && !isWarehouseTransferAutoIdLabel(name)) return name;
  const id = (params.id ?? '').trim();
  return id ? id.slice(0, 8) : '—';
}

/** AutoCRM `name` / `description` nové převodky — lidské názvy skladů (Odkud → Kam). */
export function formatWarehouseTransferAutoName(fromLabel: string, toLabel: string): string {
  const from = fromLabel.trim() || '?';
  const to = toLabel.trim() || '?';
  return `TelcoTrack převod ${from} → ${to}`;
}

/**
 * Interní auto-popisky typu `TelcoTrack vratka material_usage id=… sap=… qty=…`
 * — do UI jako lidský titulek (co se převedlo), ne dump UUID.
 */
export function humanizeWarehouseAutoDocLabel(
  text: string | null | undefined,
): string | null {
  const raw = (text ?? '').trim();
  if (!raw) return null;

  // Vratka spotřeby → příjemka: `TelcoTrack vratka material_usage id=… gi=… sap=324363 qty=1`
  if (/^TelcoTrack\s+vratka\b/i.test(raw)) {
    const sap = /\bsap=([A-Za-z0-9._/-]+)/i.exec(raw)?.[1]?.trim();
    const qtyRaw = /\bqty=(\d+(?:[.,]\d+)?)/i.exec(raw)?.[1];
    const qty = qtyRaw ? String(qtyRaw).replace(',', '.') : '';
    if (sap && qty) return `Vratka · SAP ${sap} · ${qty} ks`;
    if (sap) return `Vratka · SAP ${sap}`;
    return 'Vratka materiálu';
  }

  return null;
}

/** True když text vypadá jako technický dump (UUID / key=value), ne číslo dokladu. */
export function isWarehouseAutoDocDumpLabel(text: string | null | undefined): boolean {
  const raw = (text ?? '').trim();
  if (!raw) return false;
  if (humanizeWarehouseAutoDocLabel(raw)) return true;
  if (/^TelcoTrack\s+/i.test(raw) && /\b(id|gi|sap|qty)=/i.test(raw)) return true;
  if (isWarehouseTransferAutoIdLabel(raw)) return true;
  return false;
}

const WAREHOUSE_DOC_LIST_TITLE_MAX = 48;

function truncateWarehouseDocListTitle(label: string, max = WAREHOUSE_DOC_LIST_TITLE_MAX): string {
  const t = label.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
}

/**
 * Titulek příjemky v seznamu — lidský label místo raw `referenceIdentifier` / description.
 * Preferuje SAP+qty u vratek; jinak kompaktní poznámku / krátké číslo dokladu / id.
 */
export function formatWarehouseGoodsReceiptListTitle(params: {
  id: string;
  referenceIdentifier?: string | null;
  description?: string | null;
}): string {
  const id = (params.id ?? '').trim();
  const ref = (params.referenceIdentifier ?? '').trim();
  const desc = (params.description ?? '').trim();

  for (const candidate of [ref, desc]) {
    const human = humanizeWarehouseAutoDocLabel(candidate);
    if (human) return human;
  }

  if (ref && !isWarehouseAutoDocDumpLabel(ref)) {
    return truncateWarehouseDocListTitle(ref);
  }

  const compacted = compactWarehouseAutoDescriptionForDisplay(ref || desc);
  if (compacted && !isWarehouseAutoDocDumpLabel(compacted)) {
    return truncateWarehouseDocListTitle(compacted);
  }

  return id ? id.slice(0, 8) : '—';
}

/**
 * Titulek výdejky v seznamu — kompaktní auto-popis, jinak krátké id.
 */
export function formatWarehouseGoodsIssueListTitle(params: {
  id: string;
  name?: string | null;
  description?: string | null;
}): string {
  const id = (params.id ?? '').trim();
  const note = compactWarehouseAutoDescriptionForDisplay(
    params.name || params.description || '',
  );
  if (note && !isWarehouseAutoDocDumpLabel(note)) {
    return truncateWarehouseDocListTitle(note);
  }
  const human = humanizeWarehouseAutoDocLabel(params.name || params.description || '');
  if (human) return human;
  return id ? id.slice(0, 8) : '—';
}

/**
 * Přepíše historickou spam poznámku `SAP×qty, SAP×qty, …` na kompaktní tvar.
 * Vrací původní text, pokud vzor neodpovídá (vlastní poznámka / DL).
 */
export function compactWarehouseAutoDescriptionForDisplay(
  description: string | null | undefined,
): string {
  const raw = (description ?? '').trim();
  if (!raw) return '';
  const human = humanizeWarehouseAutoDocLabel(raw);
  if (human) return human;
  const m = /^(TelcoTrack příjem|TelcoTrack výdej|Příjem)\s+(.+)$/i.exec(raw);
  if (!m) return raw;
  const prefix = m[1];
  const tokens = m[2]
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (tokens.length < 2) return raw;

  const parsed: WarehouseStockNoteLineInput[] = [];
  for (const token of tokens) {
    const tm = /^([A-Za-z0-9._/-]+)[×x](\d+(?:[.,]\d+)?)$/i.exec(token);
    if (!tm) return raw;
    const qty = Number(String(tm[2]).replace(',', '.'));
    if (!positiveFinite(qty)) return raw;
    parsed.push({ sapCode: tm[1], quantity: qty, unit: 'm' });
  }

  // Spam = aspoň jeden SAP se opakuje (expand metrových kusů).
  const counts = new Map<string, number>();
  for (const p of parsed) counts.set(p.sapCode, (counts.get(p.sapCode) ?? 0) + 1);
  if (![...counts.values()].some((c) => c > 1)) return raw;

  const body = formatCompactWarehouseStockNoteBody(parsed);
  return body ? `${prefix} ${body}` : raw;
}
